import type { PaginatedQuery } from '@atlas/api-contract';

import { FIRST_PAGE } from '@/lib/list/list-search';

/**
 * The URL contract for the Users screen (spec R7.7).
 *
 * `GET /v1/users` takes only `search` and pagination — no sort — so this is a
 * trimmed version of the list codecs used elsewhere rather than a reuse of
 * `createListSearch`, which always carries a sort key.
 */

/** Rows per page of the roster. */
export const USERS_PAGE_SIZE = 25;

/** The URL-facing shape: every key absent when it holds its default. */
export interface UsersSearch {
  search?: string;
  page?: number;
}

const parsePage = (value: unknown): number => {
  const parsed =
    typeof value === 'number'
      ? value
      : typeof value === 'string'
        ? Number.parseInt(value, 10)
        : Number.NaN;

  return Number.isFinite(parsed) ? Math.max(FIRST_PAGE, Math.floor(parsed)) : FIRST_PAGE;
};

/** Normalise raw URL params, or an updated search object. Total, never throws. */
export const parseUsersSearch = (raw: Record<string, unknown>): UsersSearch => {
  const search = typeof raw['search'] === 'string' ? raw['search'].trim() : '';
  const page = parsePage(raw['page']);

  return {
    ...(search === '' ? {} : { search }),
    ...(page === FIRST_PAGE ? {} : { page }),
  };
};

/** The route's `validateSearch`. */
export const validateUsersSearch = (
  raw: Record<string, unknown>,
): UsersSearch => parseUsersSearch(raw);

/** Apply the defaults the URL leaves out. */
export const resolveUsersSearch = (
  search: UsersSearch,
): { search: string; page: number; pageSize: number } => ({
  search: search.search ?? '',
  page: search.page ?? FIRST_PAGE,
  pageSize: USERS_PAGE_SIZE,
});

/** Build the query the API expects, omitting an empty search term. */
export const usersToQuery = (search: UsersSearch): PaginatedQuery => {
  const resolved = resolveUsersSearch(search);

  return {
    ...(resolved.search === '' ? {} : { search: resolved.search }),
    page: resolved.page,
    pageSize: resolved.pageSize,
  };
};
